/**
 * QMS Intake Decision Panel
 * Panel for L0 (Informational) severity tickets that require intake decision
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle, XCircle, Info } from 'lucide-react';
import { useUpdateQmsTicket } from '../../hooks/useQmsTicket';
import type { QmsTicket, QmsIntakeDecision } from '../../types/qms.types';

interface QmsIntakePanelProps {
  ticket: QmsTicket;
  onDecisionComplete: () => void;
}

export function QmsIntakePanel({ ticket, onDecisionComplete }: QmsIntakePanelProps) {
  const { t } = useTranslation('qms');
  const updateMutation = useUpdateQmsTicket();

  const [notes, setNotes] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [pendingDecision, setPendingDecision] = useState<QmsIntakeDecision | null>(null);

  // Only show for L0 tickets without intake decision
  const needsIntakeDecision = ticket.severity_level === 0 && !ticket.intake_decision;

  // If decision already made, show status
  if (ticket.intake_decision) {
    const isAccepted = ticket.intake_decision === 'create_ticket';
    return (
      <div className="border-t pt-6">
        <div
          className={`p-4 rounded-lg ${
            isAccepted ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
          }`}
        >
          <div className="flex items-start gap-3">
            {isAccepted ? (
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <h4 className={`font-medium ${isAccepted ? 'text-green-800' : 'text-gray-700'}`}>
                {isAccepted
                  ? t('intake.decision.accepted')
                  : t('intake.decision.rejected')}
              </h4>
              {ticket.intake_decision_notes && (
                <p className="text-sm text-gray-600 mt-1">{ticket.intake_decision_notes}</p>
              )}
              {ticket.intake_decision_at && (
                <p className="text-xs text-gray-400 mt-2">
                  {new Date(ticket.intake_decision_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if not L0 or already has decision
  if (!needsIntakeDecision) {
    return null;
  }

  const handleDecision = async (decision: QmsIntakeDecision) => {
    try {
      await updateMutation.mutateAsync({
        ticketId: ticket._id,
        payload: {
          intake_decision: decision,
          intake_decision_notes: notes || undefined,
          // intake_decision_at and intake_decision_by are set automatically by backend
        },
      });
      onDecisionComplete();
    } catch (error) {
      console.error('Failed to submit intake decision:', error);
    }
  };

  const handleSubmit = () => {
    if (pendingDecision) {
      handleDecision(pendingDecision);
    }
  };

  return (
    <div className="border-t pt-6">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-amber-800">{t('intake.title')}</h4>
            <p className="text-sm text-amber-700 mt-1">{t('intake.description')}</p>

            {/* Info badge */}
            <div className="mt-3 flex items-center gap-2 text-xs text-amber-600">
              <Info className="h-3.5 w-3.5" />
              <span>{t('intake.level0Info')}</span>
            </div>

            {!showForm ? (
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPendingDecision('create_ticket');
                    setShowForm(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  <CheckCircle className="h-4 w-4" />
                  {t('intake.actions.accept')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPendingDecision('no_action');
                    setShowForm(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                >
                  <XCircle className="h-4 w-4" />
                  {t('intake.actions.reject')}
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <div className="text-sm font-medium text-gray-700">
                  {pendingDecision === 'create_ticket'
                    ? t('intake.confirmAccept')
                    : t('intake.confirmReject')}
                </div>

                {/* Notes field */}
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    {t('intake.notesLabel')}
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    placeholder={t('intake.notesPlaceholder')}
                  />
                </div>

                {/* Action buttons */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={updateMutation.isPending}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-colors text-sm disabled:opacity-50 ${
                      pendingDecision === 'create_ticket'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-gray-600 hover:bg-gray-700'
                    }`}
                  >
                    {updateMutation.isPending ? (
                      t('intake.actions.submitting')
                    ) : pendingDecision === 'create_ticket' ? (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        {t('intake.actions.confirmAccept')}
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4" />
                        {t('intake.actions.confirmReject')}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setPendingDecision(null);
                      setNotes('');
                    }}
                    disabled={updateMutation.isPending}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm disabled:opacity-50"
                  >
                    {t('intake.actions.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
